import {createSlice, PayloadAction} from '@reduxjs/toolkit'
import {fetchUser} from "./userSlice";
import {fetchUsers} from "./usersSlice";
import {fetchAccesses, patchAccess} from "./accessesSlice";
import {authenticate, authenticateTotp} from "./authenticatedSlice";

const initialState = {
    requestsInProgress: 0,
    fetchAccessesRequestsInProgress: 0,
    logoutNeeded: false,
    error: null,
    // incremented on every failed request, so a repeated identical error is shown again
    errorId: 0,
};

// prefer the plain-text message returned by the backend (e.g. a missing role in the target) over the generic axios one
const getErrorMessage = (action: any) => {
    const responseData = action.payload?.response?.data;
    if (typeof responseData === 'string' && responseData) {
        return responseData;
    }
    return action.payload?.message ?? action.error?.message ?? null;
};

const onRejected = (state: any, action: any) => {
    console.log(action);

    state.error = getErrorMessage(action);
    state.errorId++;

    if (typeof action.payload !== 'undefined' && typeof action.payload.response !== 'undefined') {
        if (action.payload.response.status == 401 || action.payload.response.status == 403) {
            state.logoutNeeded = true;
        }
    }
}

// a new session must not show errors (e.g. the 401 that forced the logout) left over from the previous one
const onLoggedIn = (state: any) => {
    state.error = null;
    state.logoutNeeded = false;
}

const requestStatusSlice = createSlice({
    name: 'requestStatus',
    initialState,
    reducers: {
        setLogoutNeeded: (state, action: PayloadAction<boolean>) => {
            state.logoutNeeded = action.payload;
        },
    },
    extraReducers(builder) {
        builder.addCase(authenticate.fulfilled, onLoggedIn);
        builder.addCase(authenticateTotp.fulfilled, onLoggedIn);

        builder.addCase(fetchUsers.pending, (state: any, action) => {
            state.requestsInProgress++;
        });
        builder.addCase(fetchUsers.rejected, (state: any, action) => {
            state.requestsInProgress--;
            onRejected(state, action);
        });
        builder.addCase(fetchUsers.fulfilled, (state: any, action) => {
            state.requestsInProgress--;
            state.error = null;
        });

        builder.addCase(fetchUser.pending, (state: any, action) => {
            state.requestsInProgress++;
        });
        builder.addCase(fetchUser.rejected, (state: any, action) => {
            state.requestsInProgress--;
            onRejected(state, action);
        });
        builder.addCase(fetchUser.fulfilled, (state: any, action) => {
            state.requestsInProgress--;
            state.error = null;
        });

        builder.addCase(fetchAccesses.pending, (state: any, action) => {
            state.requestsInProgress++;
            state.fetchAccessesRequestsInProgress++;
        });
        builder.addCase(fetchAccesses.rejected, (state: any, action) => {
            state.fetchAccessesRequestsInProgress--;
            state.requestsInProgress--;
            onRejected(state, action);
        })
        builder.addCase(fetchAccesses.fulfilled, (state: any, action) => {
            state.requestsInProgress--;
            state.fetchAccessesRequestsInProgress--;
            state.error = null;
        });

        builder.addCase(patchAccess.pending, (state: any, action) => {
            state.requestsInProgress++;
        });
        builder.addCase(patchAccess.rejected, (state: any, action) => {
            state.requestsInProgress--;
            onRejected(state, action);
        })
        builder.addCase(patchAccess.fulfilled, (state: any, action) => {
            state.requestsInProgress--;
            state.error = null;
        });
    }
})

export const {setLogoutNeeded} = requestStatusSlice.actions;

export default requestStatusSlice.reducer
